'use client';

import { createContext, ReactNode, RefObject, useMemo, useState } from 'react';

import { sendGTMClickEvent } from '@/components/helpers/analytics/gtm';
import { useLocation } from './locationContext';
import { useHomePlanData } from '@/components/templates/tm-home-plan-detail/hooks/useHomePlanData';
import { IModalRefProps } from '@/components/molecules/ml-modal/ml-modal.types';

const BASE_URL =
  process.env.NEXT_PUBLIC_LOGIN_URL || 'https://betawww.schumacherhomes.com';

// ===== Types =====

interface IOpenLoginModalParams {
  PlanId?: string;
  ElevationId?: string;
  StudioId?: string;
  LoginAction?: string;
  LoginRedirect?: string;
}

interface IHandleOpenParams {
  label: string;
  interactLocation: string;
  href?: string;
  params?: IOpenLoginModalParams;
}

interface ILoginModalContext {
  openLoginModal: (params: IHandleOpenParams) => void;
  setPlanIdForLogin: (id: number) => void;
  setStudioIdForLogin: (id: number) => void;
  setElevationIdForLogin: (id: number) => void;
  buildLoginUrl: (params?: IOpenLoginModalParams, href?: string) => string;
}

interface ILoginModalProviderProps {
  children: ReactNode;
  modalRef: RefObject<IModalRefProps | null>;
  setLoginUrl: (url: string) => void;
}

// ===== Context =====

const LoginModalContext = createContext<ILoginModalContext>({
  openLoginModal: () => {},
  setPlanIdForLogin: () => {},
  setStudioIdForLogin: () => {},
  setElevationIdForLogin: () => {},
  buildLoginUrl: () => BASE_URL,
});

// ===== Provider Component =====

const LoginModalProvider = ({
  children,
  modalRef,
  setLoginUrl,
}: ILoginModalProviderProps) => {
  const [planId, setPlanId] = useState(-1);
  const [studioId, setStudioId] = useState(-1);
  const [elevationId, setElevationId] = useState(-1);
  const { currentBuildStudio } = useLocation();
  const { supports3D } = useHomePlanData(planId);

  const has3D = useMemo(
    () => !!currentBuildStudio?.supports3D && !!supports3D,
    [currentBuildStudio?.supports3D, supports3D],
  );

  const getLoginUrl = (params: IOpenLoginModalParams = {}): string => {
    const searchParams = new URLSearchParams();

    if (params.PlanId) searchParams.set('PlanId', params.PlanId);
    if (params.ElevationId) searchParams.set('ElevationId', params.ElevationId);
    if (params.StudioId) searchParams.set('StudioId', params.StudioId);

    if (params.LoginAction) {
      const action =
        has3D && params.LoginAction === 'CUSTOMIZE_PLAN'
          ? 'CUSTOMIZE_PLAN_3D'
          : params.LoginAction;
      searchParams.set('LoginAction', action);
    }

    if (params.LoginRedirect) {
      searchParams.set(
        'LoginRedirect',
        encodeURIComponent(params.LoginRedirect.trim()),
      );
    }

    return `${BASE_URL}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  };

  const buildLoginUrl = (
    overrideParams?: IOpenLoginModalParams,
    href?: string,
  ): string => {
    if (!href) return getLoginUrl(overrideParams);

    const dynamicParams = substituteLoginParams(href);
    const mergedParams: IOpenLoginModalParams = {
      ...dynamicParams,
      ...overrideParams,
    };

    return getLoginUrl(mergedParams);
  };

  const openLoginModal = ({
    label,
    interactLocation,
    href,
    params,
  }: IHandleOpenParams) => {
    sendGTMClickEvent(label, interactLocation, 'form-pop-up');
    const loginUrl = buildLoginUrl(params, href);
    setLoginUrl(loginUrl);
    modalRef.current?.handleOpen();
  };

  const substituteLoginParams = (href: string): IOpenLoginModalParams => {
    const result: IOpenLoginModalParams = {};

    const query = href.split('?')[1];
    if (!query) return result;

    const params = new URLSearchParams(query);
    params.forEach((value, key) => {
      let actualValue: string | undefined;

      if (value === '{planId}' && planId !== -1) {
        actualValue = String(planId);
      } else if (value === '{elevationId}' && elevationId !== -1) {
        actualValue = String(elevationId);
      } else if (value === '{studioId}' && studioId !== -1) {
        actualValue = String(studioId);
      } else if (!value.startsWith('{')) {
        actualValue = value;
      }

      if (actualValue) {
        result[key as keyof IOpenLoginModalParams] = actualValue;
      }
    });

    return result;
  };

  return (
    <LoginModalContext.Provider
      value={{
        openLoginModal,
        setPlanIdForLogin: setPlanId,
        setStudioIdForLogin: setStudioId,
        setElevationIdForLogin: setElevationId,
        buildLoginUrl,
      }}
    >
      {children}
    </LoginModalContext.Provider>
  );
};

export { LoginModalContext, LoginModalProvider };
